import { Navigate, useLocation } from 'react-router-dom';
import { tokenStorage } from '@/services/tokenStorage';
import { ROUTES } from '@/constants/routes';
import { STORAGE_KEYS } from '@/constants/storage';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
}

function savePendingDeepLink(pathname: string, search: string) {
  if (pathname.startsWith('/tasks/') || search.includes('taskId=')) {
    sessionStorage.setItem(STORAGE_KEYS.PENDING_DEEP_LINK, `${pathname}${search}`);
  }
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const isAuthenticated = tokenStorage.isAuthenticated();

  if (!isAuthenticated) {
    savePendingDeepLink(location.pathname, location.search);
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  return children;
}

interface PublicRouteProps {
  children: ReactNode;
}

export function PublicRoute({ children }: PublicRouteProps) {
  const location = useLocation();
  const isAuthenticated = tokenStorage.isAuthenticated();
  const isPinLink =
    location.pathname === ROUTES.CREATE_PIN && new URLSearchParams(location.search).has('token');

  if (isAuthenticated && !isPinLink) {
    const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
    const pending = sessionStorage.getItem(STORAGE_KEYS.PENDING_DEEP_LINK);
    if (pending) {
      sessionStorage.removeItem(STORAGE_KEYS.PENDING_DEEP_LINK);
      return <Navigate to={pending} replace />;
    }
    if (from?.pathname && from.pathname !== ROUTES.LOGIN) {
      return <Navigate to={`${from.pathname}${from.search ?? ''}`} replace />;
    }
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return children;
}
